import type { MenuCategory, MenuItem } from '@cracked-mug/shared';
import { Heart } from 'lucide-react';
import { useFavorites } from '../../hooks/useFavorites';
import { ItemCard } from './ItemCard';

interface MenuGridProps {
  categories: MenuCategory[];
  items: MenuItem[];
  onSelectItem: (item: MenuItem) => void;
  /** Render the favorites row + the per-card heart toggles. Off on the
   *  logged-out public menu. */
  showFavorites?: boolean;
}

export function MenuGrid({ categories, items, onSelectItem, showFavorites = true }: MenuGridProps) {
  const { favoriteIds } = useFavorites();

  // Favorites that still exist on the menu (skip stale IDs the admin removed).
  const favoriteItems = favoriteIds
    .map((id) => items.find((i) => i.itemId === id))
    .filter((i): i is MenuItem => i?.isAvailable === true);

  return (
    <div className="px-4 sm:px-6 py-6 space-y-10 pb-32">
      {showFavorites && favoriteItems.length > 0 && (
        <section id="cat-favorites" aria-labelledby="cat-h-favorites" className="scroll-mt-32">
          <h2 id="cat-h-favorites" className="font-display text-3xl mb-4 flex items-center gap-2">
            <Heart size={24} className="fill-wca-red text-wca-red" aria-hidden="true" />
            Your favorites
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {favoriteItems.map((item) => (
              <ItemCard key={`fav-${item.itemId}`} item={item} onSelect={onSelectItem} />
            ))}
          </div>
        </section>
      )}

      {categories
        .filter((c) => c.isActive)
        .map((category) => {
          const categoryItems = items
            .filter((i) => i.categoryId === category.categoryId)
            .sort((a, b) => a.displayOrder - b.displayOrder);
          if (categoryItems.length === 0) return null;
          return (
            <section
              key={category.categoryId}
              id={`cat-${category.categoryId}`}
              aria-labelledby={`cat-h-${category.categoryId}`}
              className="scroll-mt-32"
            >
              <h2
                id={`cat-h-${category.categoryId}`}
                className="font-display text-3xl mb-4 flex items-center gap-2"
              >
                {category.icon.length > 0 && <span aria-hidden="true">{category.icon}</span>}
                {category.name}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryItems.map((item) => (
                  <ItemCard
                    key={item.itemId}
                    item={item}
                    onSelect={onSelectItem}
                    showFavorite={showFavorites}
                  />
                ))}
              </div>
            </section>
          );
        })}
    </div>
  );
}
